import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gql } from '@apollo/client';
import apolloClient from '../../graphql/apollo-client';
import { GET_INQUIRIES } from '../../graphql/queries';
import { DELETE_INQUIRY } from '../../graphql/mutations';
import { useAuthStore } from '../../store/authStore';

const InquiryListPage: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [inquiries, setInquiries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchInquiries = useCallback(async () => {
    if (!user?.userId) return;
    try {
      setLoading(true);
      const res = await apolloClient.query({
        query: gql`${GET_INQUIRIES}`,
        variables: { userId: user.userId },
        fetchPolicy: 'network-only',
      });
      setInquiries(res.data?.getInquiries?.inquiries || []);
    } catch (err) { console.error('Failed to fetch inquiries:', err); }
    finally { setLoading(false); }
  }, [user?.userId]);

  useEffect(() => { fetchInquiries(); }, [fetchInquiries]);

  const handleDelete = async (inquiryId: string) => {
    if (!window.confirm(t('inquiry.confirmDelete'))) return;
    try {
      await apolloClient.mutate({ mutation: gql`${DELETE_INQUIRY}`, variables: { inquiryId } });
      fetchInquiries();
    } catch (err) { console.error('Failed to delete:', err); }
  };

  const statusColor = (status: string) => {
    if (status === '답변완료') return 'bg-green-100 text-green-700';
    if (status === '처리중') return 'bg-yellow-100 text-yellow-700';
    return 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-xl font-bold text-gray-900">{t('inquiry.title')}</h1>
        <button onClick={() => navigate('/inquiry/new')}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
          {t('inquiry.write')}
        </button>
      </div>
      <hr className="border-gray-200 mb-6" />

      {loading ? (
        <div className="text-center py-8 text-gray-400">{t('loading')}</div>
      ) : inquiries.length === 0 ? (
        <div className="text-center py-8 text-gray-400">{t('inquiry.empty')}</div>
      ) : (
        <div className="space-y-0">
          {inquiries.map((inq) => (
            <div key={inq.inquiryId} className="border-b border-gray-100 py-4 px-2 -mx-2 hover:bg-gray-50 rounded transition-colors">
              <div className="flex items-center gap-3 mb-1">
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${statusColor(inq.status)}`}>
                  {inq.status}
                </span>
                <span onClick={() => navigate(`/inquiry/${inq.inquiryId}`)}
                  className="text-sm font-bold text-gray-900 flex-1 truncate cursor-pointer hover:text-blue-600">
                  {inq.title}
                </span>
                <span className="text-xs text-gray-400 flex-shrink-0">{inq.createdAt?.slice(0, 10)}</span>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500 truncate flex-1 pl-[calc(2rem+12px)]">{inq.content}</p>
                <button onClick={() => handleDelete(inq.inquiryId)}
                  className="ml-3 px-3 py-1 text-xs font-medium text-red-600 border border-red-300 rounded hover:bg-red-50 flex-shrink-0">
                  {t('delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InquiryListPage;
